import { useCallback, useEffect, useMemo, useState } from "react";
import { Calendar, ChevronDown, ChevronRight, Plus, Search, X, Phone, MessageCircle, HandCoins, XCircle, Check } from "lucide-react";
import CalendarService from "../../../services/calendar/CalendarService";
import { confirmDialog, showError, showSuccess, showWarning, notifyApiError } from "../../../components/alertas";
import Sheet from "../../../components/sheet/Sheet";
import useMediaQuery from "../../../hooks/useMediaQuery";
import AppointmentSheet from "./AppointmentSheet";
import logger from "../../../utils/logger";
import "./citas.css";

const STAFF_COLORS = [
    { bg: '#dcfce7', text: '#166534' },
    { bg: '#dbeafe', text: '#1e40af' },
    { bg: '#fce7f3', text: '#9d174d' },
    { bg: '#ede9fe', text: '#5b21b6' },
    { bg: '#ffedd5', text: '#9a3412' },
];

function getStaffColor(staffId, staffList) {
    const idx = (staffList || []).findIndex(s => s.id === staffId);
    return STAFF_COLORS[(idx < 0 ? 0 : idx) % STAFF_COLORS.length];
}

function getDayLabel(date) {
    if (!date) return { prefix: null, full: "Sin fecha", isToday: false, isTomorrow: false };
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    const isToday = date.toDateString() === today.toDateString();
    const isTomorrow = date.toDateString() === tomorrow.toDateString();
    const weekday = date.toLocaleDateString("es-MX", { weekday: "long" });
    const dayMonth = date.toLocaleDateString("es-MX", { day: "numeric", month: "long" });
    return {
        prefix: isToday ? "Hoy" : isTomorrow ? "Mañana" : null,
        full: `${weekday.charAt(0).toUpperCase() + weekday.slice(1)}, ${dayMonth}`,
        isToday,
        isTomorrow,
    };
}

const money = (n) => `$${(parseFloat(n) || 0).toLocaleString("es-MX", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

/**
 * Citas: una sola lista (móvil y escritorio) agrupada por día; cada fila abre un
 * sheet con el detalle y las acciones (confirmar anticipo, cancelar, llamar,
 * WhatsApp). "Nueva cita" abre el sheet de alta. Las anclas data-tour se conservan.
 */
function AppointmentsTab({ clientData, userData }) {
    const clientId = clientData?.id;
    const isAdmin = userData?.userType === 'admin';
    const isDesktop = useMediaQuery("(min-width: 992px)");
    // Cliente recién creado sin profesionales: no hay agenda que consultar.
    const adminHasNoStaff = isAdmin && (clientData?.staff?.length ?? 0) === 0;

    const getDefaultStaffId = () => {
        if (isAdmin) return clientData?.staff?.[0]?.id || "";
        const userStaff = clientData?.staff?.find(s => s.name === userData.username);
        return userStaff?.id || "";
    };

    const [appointments, setAppointments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);
    const [selectedStaffId, setSelectedStaffId] = useState(getDefaultStaffId());
    const [showNew, setShowNew] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [collapsedDays, setCollapsedDays] = useState(new Set());
    const [selected, setSelected] = useState(null); // cita abierta en el sheet de detalle

    // Sincroniza el staffId del staff (no-admin) cuando carga clientData tardíamente
    useEffect(() => {
        if (isAdmin) return;
        if (!clientData?.staff || clientData.staff.length === 0) return;
        const userStaff = clientData?.staff?.find(s => s.name === userData?.username);
        if (userStaff?.id && selectedStaffId !== userStaff.id) setSelectedStaffId(userStaff.id);
        // selectedStaffId se omite a propósito: incluirlo re-dispararía el efecto al cambiar
        // la selección e interferiría con la selección libre del usuario.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientData, isAdmin, userData]);

    const toggleDay = (dayKey) => {
        setCollapsedDays((prev) => {
            const next = new Set(prev);
            next.has(dayKey) ? next.delete(dayKey) : next.add(dayKey);
            return next;
        });
    };

    const fetchAppointments = useCallback(async () => {
        setLoading(true);
        try {
            const res = await CalendarService.getAppointments(clientId, selectedStaffId);
            setAppointments(res.data || []);
        } catch (error) {
            logger.error("Error fetching appointments:", error);
            if (error.message.includes("no tiene Google Calendar conectado")) {
                showWarning("Sin calendario", "No se ha conectado ningún calendario para este miembro del equipo. Por favor, conecta un calendario para gestionar las citas.");
            } else {
                showError("Error", error);
            }
            setAppointments([]);
        } finally {
            setLoading(false);
        }
    }, [clientId, selectedStaffId]);

    useEffect(() => {
        if (!clientId) return;
        if (adminHasNoStaff) return; // sin profesionales: no llamar al backend (evita el swal de error)
        fetchAppointments();
    }, [clientId, selectedStaffId, fetchAppointments, adminHasNoStaff]);

    const normalizedAppointments = useMemo(() => {
        const formatDate = (dateStr) => (dateStr ? new Date(dateStr).toLocaleDateString() : "-");
        const formatTime = (dateStr) => dateStr ? new Date(dateStr).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "-";
        // El precio guardado en el evento puede venir vacío o en "0" (citas del bot o
        // anteriores a que se guardara): se completa con el precio actual del servicio.
        const servicePrice = (name) => {
            const svc = clientData?.business?.services?.find(s => s.name?.toLowerCase().trim() === (name || "").toLowerCase().trim());
            return Number(svc?.price) || null;
        };
        return (appointments || []).map((appointment, idx) => {
            const extendedProps = appointment.extendedProperties?.private || {};
            const nombre = extendedProps.nombre || appointment.summary?.replace(/^Cita de\s*/i, "") || "-";
            const telefono = extendedProps.telefono || appointment.telefono || "-";
            const servicio = extendedProps.servicio || (() => {
                const match = appointment.description?.match(/Servicio:\s*(.+)/i);
                return match ? match[1].split("\n")[0] : "-";
            })();
            const status = appointment.status === "confirmed" ? "confirmada" : (appointment.status || "pendiente");
            return {
                ...appointment, nombre, telefono, servicio,
                fecha: formatDate(appointment.start), hora: formatTime(appointment.start), status,
                paymentStatus: extendedProps.paymentStatus, advanceAmount: extendedProps.advanceAmount,
                price: Number(extendedProps.price) || servicePrice(servicio),
                blockEventId: appointment.id, staffId: appointment.staffId || "", key: appointment.id || idx,
            };
        });
    }, [appointments, clientData]);

    const handleCancel = async (appointment) => {
        const confirmed = await confirmDialog({
            title: `¿Cancelar la cita de ${appointment.nombre}?`,
            details: [["Servicio", appointment.servicio], ["Fecha", appointment.fecha], ["Hora", appointment.hora]],
            note: "Esta acción no se puede deshacer.",
            confirmButtonText: "Sí, cancelar",
            cancelButtonText: "No",
            icon: "warning",
            danger: true,
        });
        if (!confirmed) return;
        setBusyId(appointment.id);
        try {
            await CalendarService.cancelAppointment(appointment.id, clientId, selectedStaffId || appointment.staffId);
            showSuccess("Cita cancelada", `La cita de ${appointment.nombre} ha sido cancelada correctamente.`);
            setSelected(null);
            fetchAppointments();
        } catch (error) {
            notifyApiError(error, "Cancelar cita");
        } finally {
            setBusyId(null);
        }
    };

    const handleConfirmPayment = async (appointment) => {
        const precioTotal = parseFloat(appointment.price) || 0;
        const anticipo = parseFloat(appointment.advanceAmount) || 0;
        const restante = precioTotal - anticipo;
        const confirmed = await confirmDialog({
            title: `¿Confirmar el pago de ${appointment.nombre}?`,
            details: [
                ["Servicio", appointment.servicio],
                ["Fecha", appointment.fecha],
                ["Hora", appointment.hora],
                ["Precio total", money(precioTotal)],
                ["Anticipo pagado", money(anticipo)],
                { label: "Restante en cita", value: money(restante), tone: "accent" },
            ],
            note: "Se notificará al cliente cuando confirmes.",
            noteTone: "success",
            confirmButtonText: "Sí, confirmar pago",
            cancelButtonText: "No",
            icon: "question",
        });
        if (!confirmed) return;
        setBusyId(appointment.id);
        try {
            await CalendarService.confirmPendingPayment(appointment.blockEventId, appointment.staffId, appointment.telefono, clientId);
            showSuccess("Éxito", "Pago confirmado y cliente notificado");
            setSelected(null);
            fetchAppointments();
        } catch (error) {
            notifyApiError(error, "Confirmar pago");
        } finally {
            setBusyId(null);
        }
    };

    const filteredAppointments = useMemo(() => {
        if (!searchTerm.trim()) return normalizedAppointments;
        const term = searchTerm.toLowerCase();
        return normalizedAppointments.filter(apt => apt.nombre.toLowerCase().includes(term) || apt.telefono.toLowerCase().includes(term) || apt.servicio.toLowerCase().includes(term));
    }, [normalizedAppointments, searchTerm]);

    const pendingCount = useMemo(() => filteredAppointments.filter(apt => apt.paymentStatus === "pending").length, [filteredAppointments]);

    const groupedByDay = useMemo(() => {
        const groups = {};
        filteredAppointments.forEach(apt => {
            const startDate = apt.start ? new Date(apt.start) : null;
            const dateKey = startDate ? startDate.toDateString() : "__nodate__";
            if (!groups[dateKey]) groups[dateKey] = { date: startDate, appointments: [] };
            groups[dateKey].appointments.push(apt);
        });
        return Object.values(groups)
            .sort((a, b) => { if (!a.date) return 1; if (!b.date) return -1; return a.date - b.date; })
            .map(group => ({
                ...group,
                dayKey: group.date?.toDateString() || "__nodate__",
                appointments: [...group.appointments].sort((a, b) => new Date(a.start) - new Date(b.start)),
            }));
    }, [filteredAppointments]);

    const staffName = (id) => clientData?.staff?.find(s => s.id === id)?.name;
    const showStaffFilter = isAdmin && (clientData?.staff?.length || 0) > 1;
    const totalFiltered = filteredAppointments.length;
    const totalAll = appointments.length;

    if (adminHasNoStaff) {
        return (
            <section className="cp-card">
                <div className="cp-empty">
                    <Calendar size={40} />
                    <h3>Sin profesionales conectados</h3>
                    <p>Conecta un profesional con su cuenta de Google en Conexiones para empezar a agendar.</p>
                </div>
            </section>
        );
    }

    return (
        <section className="cp-appts">
            <header className="cp-page-head">
                <div>
                    <h2 className="cp-page-title" data-tour="citas.title">
                        Citas
                        {!loading && <span className="cp-count">{totalAll !== totalFiltered ? `${totalFiltered} de ${totalAll}` : totalAll}</span>}
                    </h2>
                </div>
                <div className="cp-page-actions">
                    <button type="button" className="cp-btn cp-btn-primary" onClick={() => setShowNew(true)} data-tour="citas.new">
                        <Plus size={18} /> <span>Nueva cita</span>
                    </button>
                </div>
            </header>

            <div className="cp-sticky cp-appts-toolbar">
                {showStaffFilter && (
                    <div className="cp-segment-scroll" data-tour="citas.staff">
                        <div className="cp-segment" role="tablist" aria-label="Profesional">
                            <button type="button" role="tab" aria-selected={selectedStaffId === ""} className={`cp-segment-btn ${selectedStaffId === "" ? "is-active" : ""}`} onClick={() => setSelectedStaffId("")}>Todos</button>
                            {clientData.staff.map(staff => (
                                <button key={staff.id} type="button" role="tab" aria-selected={selectedStaffId === staff.id} className={`cp-segment-btn ${selectedStaffId === staff.id ? "is-active" : ""}`} onClick={() => setSelectedStaffId(staff.id)}>{staff.name}</button>
                            ))}
                        </div>
                    </div>
                )}
                <div className="cp-input-wrap" data-tour="citas.search">
                    <Search size={17} />
                    <input type="search" className="cp-input" placeholder="Buscar por nombre, teléfono o servicio" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} aria-label="Buscar citas" autoComplete="off" />
                    {searchTerm && <button type="button" className="cp-input-clear" onClick={() => setSearchTerm("")} aria-label="Limpiar búsqueda"><X size={16} /></button>}
                </div>
            </div>

            {pendingCount > 0 && (
                <div className="cp-banner" role="status" data-tour="citas.pending">
                    <HandCoins size={18} />
                    <div><strong>{pendingCount} anticipo{pendingCount > 1 ? "s" : ""} por confirmar.</strong> Toca la cita y confirma el pago cuando veas el comprobante.</div>
                </div>
            )}

            <div data-tour="citas.list" className="cp-appts-list">
                {loading ? (
                    <div className="cp-stack">
                        {[0, 1, 2].map(i => <div key={i} className="cp-skeleton" style={{ height: 64 }} />)}
                    </div>
                ) : groupedByDay.length === 0 ? (
                    <div className="cp-card">
                        <div className="cp-empty">
                            <Calendar size={36} />
                            <h3>{searchTerm ? "Nada coincide" : "Sin citas por ahora"}</h3>
                            <p>{searchTerm ? "Prueba con otro nombre, teléfono o servicio." : "Las que agenden tus clientes por WhatsApp o por tu link aparecerán aquí."}</p>
                        </div>
                    </div>
                ) : groupedByDay.map(({ date, dayKey, appointments: dayApts }) => {
                    const { isToday, isTomorrow, prefix, full } = getDayLabel(date);
                    const isCollapsed = collapsedDays.has(dayKey);
                    const dayPending = dayApts.filter(a => a.paymentStatus === "pending").length;
                    return (
                        <div key={dayKey} className={`cp-day ${isToday ? "is-today" : ""} ${isTomorrow ? "is-tomorrow" : ""}`}>
                            <button type="button" className="cp-day-head" onClick={() => toggleDay(dayKey)} aria-expanded={!isCollapsed}>
                                {isCollapsed ? <ChevronRight size={16} className="cp-chev" /> : <ChevronDown size={16} className="cp-chev" />}
                                {prefix && <span className="cp-pill cp-pill-accent">{prefix}</span>}
                                <span className="cp-day-title">{full}</span>
                                <span className="cp-day-meta">
                                    {dayPending > 0 && <span className="cp-pill cp-pill-warn"><HandCoins size={12} /> {dayPending}</span>}
                                    <span className="cp-count">{dayApts.length}</span>
                                </span>
                            </button>
                            {!isCollapsed && (
                                <ul className="cp-list">
                                    {dayApts.map(apt => {
                                        const isPending = apt.paymentStatus === "pending";
                                        const who = !selectedStaffId && apt.staffId ? staffName(apt.staffId) : null;
                                        const color = who ? getStaffColor(apt.staffId, clientData?.staff) : null;
                                        return (
                                            <li key={apt.key}>
                                                <button type="button" className={`cp-row cp-appt ${isPending ? "is-pending" : ""}`} onClick={() => setSelected(apt)}>
                                                    <span className="cp-appt-time">{apt.hora}</span>
                                                    <span className="cp-row-main">
                                                        <span className="cp-row-title">{apt.nombre}</span>
                                                        <span className="cp-row-sub">
                                                            <span className="cp-appt-service">{apt.servicio}</span>
                                                            {who && <span className="cp-appt-staff" style={{ background: color.bg, color: color.text }}>{who}</span>}
                                                        </span>
                                                    </span>
                                                    <span className="cp-row-end">
                                                        {isPending
                                                            ? <span className="cp-pill cp-pill-warn"><HandCoins size={12} /> {money(apt.advanceAmount)}</span>
                                                            : <span className={`cp-pill ${apt.status === "confirmada" ? "cp-pill-ok" : ""}`}>{apt.status}</span>}
                                                        <ChevronRight size={18} className="cp-chev" />
                                                    </span>
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Detalle de la cita: acciones al tocar */}
            <Sheet open={!!selected} onClose={() => setSelected(null)} side={isDesktop ? "right" : "bottom"} title={selected?.nombre || "Cita"} subtitle={selected ? `${selected.fecha} · ${selected.hora}` : ""} className="cp-vars"
                footer={selected && (
                    <div className={`cp-sheet-actions ${selected.paymentStatus === "pending" ? "" : "is-single"}`}>
                        {selected.paymentStatus === "pending" && (
                            <button type="button" className="cp-btn cp-btn-primary" disabled={busyId === selected.id} onClick={() => handleConfirmPayment(selected)}>
                                {busyId === selected.id ? <span className="cp-spin" /> : <Check size={16} />} Confirmar pago
                            </button>
                        )}
                        <button type="button" className="cp-btn cp-btn-danger" disabled={busyId === selected.id} onClick={() => handleCancel(selected)}>
                            {busyId === selected.id ? <span className="cp-spin" /> : <XCircle size={16} />} Cancelar cita
                        </button>
                    </div>
                )}
            >
                {selected && (
                    <div className="cp-appt-detail">
                        {selected.paymentStatus === "pending" ? (
                            <div className="cp-note cp-note-accent">
                                <HandCoins size={18} />
                                <div><strong>Anticipo de {money(selected.advanceAmount)} por confirmar.</strong> La cita queda definitiva cuando confirmes el pago; el cliente recibe su confirmación al momento.</div>
                            </div>
                        ) : (
                            <span className={`cp-pill ${selected.status === "confirmada" ? "cp-pill-ok" : ""}`}>{selected.status}</span>
                        )}
                        <dl className="cp-kv">
                            <dt>Servicio</dt><dd>{selected.servicio}</dd>
                            <dt>Fecha</dt><dd>{getDayLabel(selected.start ? new Date(selected.start) : null).full}</dd>
                            <dt>Hora</dt><dd>{selected.hora}</dd>
                            {selected.staffId && staffName(selected.staffId) && (<><dt>Atiende</dt><dd>{staffName(selected.staffId)}</dd></>)}
                            {selected.price > 0 && (<><dt>Precio</dt><dd>{money(selected.price)}</dd></>)}
                            <dt>Teléfono</dt>
                            <dd>{selected.telefono && selected.telefono !== "-" ? selected.telefono : <span className="cp-muted">Sin teléfono</span>}</dd>
                        </dl>
                        {selected.telefono && selected.telefono !== "-" && (
                            <div className="cp-appt-contact">
                                <a className="cp-btn" href={`tel:${selected.telefono}`}><Phone size={16} /> Llamar</a>
                                <a className="cp-btn" href={`https://wa.me/${selected.telefono.replace(/\D/g, "")}`} target="_blank" rel="noreferrer"><MessageCircle size={16} /> WhatsApp</a>
                            </div>
                        )}
                    </div>
                )}
            </Sheet>

            <AppointmentSheet open={showNew} onClose={() => setShowNew(false)} clientData={clientData} onSuccess={fetchAppointments} />
        </section>
    );
}

export default AppointmentsTab;
